import { TrendingUp } from '@mui/icons-material';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { LineChart } from '@mui/x-charts';
import React, { useState } from 'react';

import EnhacedBox from '../../../../../../../lib/designSystem/Box';

interface PaymentTrendsChartProps {
  data: {
    paymentTrends?: Array<{
      date: string;
      payments: number;
      amount: number;
    }>;
  };
}

const PaymentTrendsChart: React.FC<PaymentTrendsChartProps> = ({ data }) => {
  const [viewMode, setViewMode] = useState<'count' | 'amount'>('count');

  if (!data?.paymentTrends || data.paymentTrends.length === 0) {
    return (
      <EnhacedBox small>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(7, 210, 254, 0.12)',
            }}
          >
            <TrendingUp sx={{ fontSize: 18, color: '#07D2FE' }} />
          </div>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Tendencias de Pagos
          </Typography>
        </div>
        <Box
          sx={{
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No hay datos disponibles para mostrar las tendencias de pagos
          </Typography>
        </Box>
      </EnhacedBox>
    );
  }

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: 'count' | 'amount' | null,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const chartData = data.paymentTrends.map((trend) => ({
    date: trend.date,
    value: viewMode === 'count' ? trend.payments : trend.amount,
  }));

  const isCountView = viewMode === 'count';

  return (
    <EnhacedBox>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(7, 210, 254, 0.12)',
          }}
        >
          <TrendingUp sx={{ fontSize: 18, color: '#07D2FE' }} />
        </div>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.1rem',
          }}
        >
          Tendencias de Pagos
        </Typography>
      </div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isCountView
          ? 'Evolución de la cantidad de pagos por período'
          : 'Evolución del monto total de pagos por período'}
      </Typography>

      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '140px',
            },
          }}
        >
          <ToggleButton value="count">Cantidad de Pagos</ToggleButton>
          <ToggleButton value="amount">Monto de Pagos</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Chart */}
      <Box sx={{ height: 300, width: '100%' }}>
        <LineChart
          xAxis={[
            {
              data: chartData.map((item: any) => item.date),
              scaleType: 'band',
              valueFormatter: (value: string) => value,
            },
          ]}
          series={[
            {
              data: chartData.map((item: any) => item.value),
              area: false,
              curve: 'monotoneX',
              color: isCountView ? '#07D2FE' : '#FFC400',
              showMark: true,
            },
          ]}
          height={300}
          grid={{ horizontal: true }}
          slotProps={{
            legend: { hidden: true },
          }}
          yAxis={[
            {
              valueFormatter: (value: number) => {
                if (isCountView) {
                  return value.toString();
                } else {
                  return `${(value / 1000000).toFixed(1)}M`;
                }
              },
            },
          ]}
        />
      </Box>
    </EnhacedBox>
  );
};

export default PaymentTrendsChart;
